/**
 * How the user adjusts a placed wheel.
 *
 * Was the ?touchUi flag; promoted to a runtime setting on 2026-08-23
 * with touch as the DEFAULT. Direct manipulation is the mode people
 * reach for, and the arrow pad is now the fallback for anyone who wants
 * discrete nudges — so it lives in a settings popover rather than a URL
 * parameter nobody outside the team would ever type.
 *
 * It has to be runtime rather than construction-time, because three
 * separate things react to it: the renderer draws the selection ring,
 * the controller decides whether placement auto-selects, and the canvas
 * gesture wiring decides whether drags move a wheel. A store keeps them
 * reading one value instead of each caching a copy taken at boot.
 */

export type WheelAdjustMode = 'touch' | 'arrows'

export interface WheelAdjustModeStore {
  get(): WheelAdjustMode
  set(mode: WheelAdjustMode): void
  subscribe(listener: () => void): () => void
}

export function createWheelAdjustModeStore(
  initial: WheelAdjustMode = 'touch',
): WheelAdjustModeStore {
  let mode: WheelAdjustMode = initial
  const listeners = new Set<() => void>()

  return {
    get() {
      return mode
    },
    set(next) {
      // Bail on a no-op so useSyncExternalStore subscribers are not
      // woken for a setting that did not change.
      if (next === mode) return
      mode = next
      for (const listener of Array.from(listeners)) listener()
    },
    subscribe(listener) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}
